import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  Paper,
  Popper,
  PopperPlacementType
} from "@mui/material";
import { debounce } from "lodash";
import React, {
  useState,
} from "react";
import { useProcessStatusTracking } from "utils/context/ProcessStatusContext";
import { useFormContext } from "react-hook-form";
import AutocompleteTextField from "./AutocompleteTextField";
import { AutocompleteResponse } from "entities/UIModel";
import LightTooltip from "controls/global/light-tooltip";
import { hasValue, REGEX_NONE_ASCII } from "utils/shared";
import {
    colors,
    boxShadows,
} from "theme/defaultStyle";

interface Props<T> {
  label: string;
  loading?: boolean;
  name: string;
  value?: any;
  placeholder?: string;
  freeSolo?: boolean;
  minLengthBeforeSearch?: number;
  disabled?: boolean;
  maxLength?: number;
  stateName?: string;
  hasError?: boolean;
  milisecondsToDelay?: number;
  onFocus?: (event: React.ChangeEvent<any>) => void;
  onBlur?: (event: React.ChangeEvent<any>) => void;
  onBlurForm?: () => void;
  onValueChanged: (value: any) => void;
  onGetOptions?: (inputText: string) => Promise<T[]>;
  onTypeInput?: (
    inputText: string,
    reason: AutocompleteInputChangeReason
  ) => void;
  getOptionSelected: (option: any, value: any) => boolean;
  getOptionLabel?: (option: any) => string;
  options?: AutocompleteResponse[];
  open?: boolean;
  debounceTime?: number;
  fieldRef?: any;
  popperPlacement?: PopperPlacementType | undefined;
  size?: "small" | "medium";
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: any,
    state: any,
    ownerState: any,
  ) => React.ReactNode;
  resetDefaultValue?: string;
}

const AutocompleteInput = <T,>({
  label,
  loading = false,
  name,
  value,
  placeholder,
  minLengthBeforeSearch = 1,
  freeSolo = true,
  disabled = false,
  maxLength,
  stateName,
  hasError = false,
  milisecondsToDelay = 500,
  onBlur,
  onBlurForm,
  onFocus,
  onValueChanged,
  onGetOptions,
  onTypeInput,
  getOptionLabel,
  getOptionSelected,
  renderOption,
  options,
  debounceTime = 500,
  fieldRef,
  popperPlacement,
  resetDefaultValue,
  size = "medium",
  ...rest
}: Props<T>) => {
  const [shrinkable, setShrinkable] = useState<boolean>(false);
  const [_options, setOptions] = React.useState<any[]>([]);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [, { setBypassDefaultLoader }] = useProcessStatusTracking();
  const searchRequestRef = React.useRef(0);
  const { getValues } = useFormContext();
  const relatedStateField = stateName
    ? getValues(`${stateName}` as const)
    : undefined;

  const handleOnChange = (_: React.ChangeEvent<any>, newValue: any) => {
    if (newValue && typeof newValue !== "string") {
      setOptions([
        newValue,
        ..._options.filter((option) => option !== newValue),
      ]);
    }
    setShrinkable(true);
    onValueChanged && onValueChanged(newValue);
  };

  const handleOnBlur = (e: React.ChangeEvent<any>) => {
    const currentValue = e.target.value;
    setShrinkable(hasValue(currentValue) || hasError);
    if (currentValue !== value) {
      if (!freeSolo) {
        return;
      }

      onValueChanged(currentValue);
    }

    onBlur && onBlur(e);
    onBlurForm && onBlurForm();
  };

  const handleOnInputChange = async (
    _: React.ChangeEvent<any>,
    newInputValue: string,
    reason: AutocompleteInputChangeReason
  ) => {
    // Only what the user types should trigger a lookup. MUI also fires this
    // with reason "reset" right after an option is picked (and on programmatic
    // form resets), which would otherwise re-search the text it just filled in.
    if (reason !== "input") {
      if (!newInputValue) {
        searchRequestRef.current += 1;
        setOptions([]);
      }
      // Always notify: callers key off the "clear" reason.
      onTypeInput?.(newInputValue, reason);
      return;
    }

    const newInputValue1 =
      newInputValue?.replace(REGEX_NONE_ASCII, "")?.trim() ?? "";
    if (newInputValue1.length < minLengthBeforeSearch) {
      // Invalidate anything in flight so a late response cannot repopulate
      // the list the user just cleared.
      searchRequestRef.current += 1;
      setOptions([]);
    } else if (onGetOptions) {
      const requestId = ++searchRequestRef.current;
      setBypassDefaultLoader(true);
      try {
        const results = await onGetOptions(newInputValue1);
        // Ignore out-of-order responses: only the newest request may paint.
        if (requestId === searchRequestRef.current) {
          setOptions(results);
        }
      } catch (error) {
        console.error("ERROR: AutocompleteInput: onGetOptions.", error);
        if (requestId === searchRequestRef.current) {
          setOptions([]);
        }
      } finally {
        setTimeout(() => setBypassDefaultLoader(false), 500);
      }
    }
    onTypeInput?.(newInputValue, reason);
  };

  // Keep the debounced callback identity stable. Re-creating it on every
  // render left previously scheduled calls uncancelled, so a single burst of
  // typing could fire several overlapping searches whose responses raced.
  const inputChangeHandlerRef = React.useRef(handleOnInputChange);
  inputChangeHandlerRef.current = handleOnInputChange;

  const debouncedInputChange = React.useMemo(
    () =>
      debounce(
        (
          event: React.SyntheticEvent,
          inputValue: string,
          reason: AutocompleteInputChangeReason
        ) => {
          void inputChangeHandlerRef.current(
            event as React.ChangeEvent<any>,
            inputValue,
            reason
          );
        },
        debounceTime
      ),
    [debounceTime]
  );

  React.useEffect(
    () => () => debouncedInputChange.cancel(),
    [debouncedInputChange]
  );

  // React.useEffect(() => {
  //   setShrinkable(hasError || hasValue(value));
  // }, [value, hasError]);

  React.useEffect(() => {
    setOptions([]);
  }, [relatedStateField]);

  const handleFocus = () => {
    setShrinkable(true);
  };

  const handleOpenTooltip = (event: any) => {
    const inputElem = event.target;
    const compare =
      inputElem && inputElem?.scrollWidth > inputElem?.clientWidth;
    setIsTooltipOpen(compare);
  };

  // console.log("---Rerender -- Shrinkable:",
  //   JSON.stringify({
  //     shrinkable: shrinkable, 
  //     value: value, 
  //     hasError: hasError
  //   }));

  return (
    <>
      <Autocomplete
        getOptionLabel={getOptionLabel}
        // MUI v5 renamed getOptionSelected to isOptionEqualToValue. Without
        // this it fell through to ...rest onto the DOM node and MUI silently
        // used reference equality. Strings (freeSolo input, and the string
        // form value) compare by identity so they never match an object
        // option, which keeps filterSelectedOptions behaving as before.
        isOptionEqualToValue={(option: any, optionValue: any) =>
          typeof option === "string" || typeof optionValue === "string"
            ? option === optionValue
            : getOptionSelected(option, optionValue)
        }
        options={options || _options || []}
        filterOptions={(x) => x}
        autoComplete
        disabled={disabled}
        onMouseEnter={handleOpenTooltip}
        onMouseLeave={() => setIsTooltipOpen(false)}
        includeInputInList
        filterSelectedOptions
        disableClearable
        noOptionsText="NO DATA FOUND"
        loading={loading}
        value={value || ""}
        // value={typeof value === "string" ? "" : value}
        size={size}
        onChange={handleOnChange}
        onFocus={onFocus}
        onBlur={handleOnBlur}
        freeSolo={freeSolo}
        handleHomeEndKeys={false}
        onInputChange={debouncedInputChange}
        {...rest}
        renderInput={(params: AutocompleteRenderInputParams) => {
          let defaultValue = params.inputProps.value;
          if (resetDefaultValue === name && disabled) {
            defaultValue = params.inputProps.value || value;
          }

          return (
            <LightTooltip title={value || ""} open={isTooltipOpen} arrow>
              <AutocompleteTextField
                {...params}
                name={name}
                label={label}
                error={hasError}
                onFocus={handleFocus}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                }}
                inputProps={{
                  ...params.inputProps,
                  value: defaultValue,
                  placeholder: placeholder,
                  maxLength: maxLength,
                  "aria-errormessage": hasError ? "rhfvalidationerror" : "",
                }}
                InputLabelProps={{
                  shrink: Boolean(shrinkable),
                }}
                variant="outlined"
                inputRef={fieldRef}
              />
            </LightTooltip>
          );
        }}
        renderOption={renderOption}
        PaperComponent={(props) => (
          <Paper
            {...props}
            style={{
              padding: 0,
              margin: 0,
              border: `0px solid ${colors.blue15}`,
              boxShadow: `${boxShadows.boxShadow03}`,
            }}
          />
        )}
        PopperComponent={({ open, ...props }) => (
          <Popper
            {...props}
            open={options?.length || _options?.length || loading ? open : false}
            placement={popperPlacement}
          />
        )}
      />
    </>
  );
};

export default AutocompleteInput;
